import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Avatar,
  IconButton,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  ButtonBase
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  People,
  CalendarToday,
  Warning,
  Message,
  MonitorHeart,
  Phone,
  VideoCall,
  TrendingUp,
  AccessTime,
  CheckCircle,
  PictureAsPdf
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Pie,
  Cell,
  PieChart,
  Legend
} from 'recharts';
import axios from 'axios';
import { Patient, Appointment } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

// Utility function to fix S3 URL format
const fixS3Url = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  // Check if it's an S3 URL with regional endpoint
  const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3RegionalPattern);

  if (match) {
    const bucketName = match[1];
    const path = match[3];

    // Convert to path-style URL (works better with CORS)
    const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
    console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
    return fixedUrl;
  }

  // If already in standard format, return as is
  return url;
};

// Utility function to get cache-busted image URL
const getCacheBustedImageUrl = (url: string | undefined, timestamp?: number): string | undefined => {
  if (!url) return undefined;

  const fixedUrl = fixS3Url(url);
  if (!fixedUrl) return undefined;

  const ts = timestamp || Date.now();
  return `${fixedUrl}?t=${ts}`;
};

const COLORS = ['#f44336', '#ff9800', '#4caf50'];

const StatCard = React.memo(({ title, value, icon, color, subtitle, onClick }: any) => (
  <Card
    elevation={2}
    onClick={onClick}
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease-in-out',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: 4,
        '& .icon-box': {
          transform: 'scale(1.1)',
          opacity: 1
        }
      } : {}
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4" component="h2" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          className="icon-box"
          sx={{
            color,
            opacity: 0.8,
            transition: 'transform 0.2s ease-in-out'
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
));

const DoctorProfileCard = React.memo(({ user, imageUrl }: any) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            src={imageUrl}
            imgProps={{
              crossOrigin: "anonymous",
              onError: (e) => {
                console.error('❌ Doctor avatar image failed to load:', imageUrl);
              }
            }}
            sx={{
              width: 80,
              height: 80,
              bgcolor: imageUrl ? 'transparent' : 'primary.main',
              fontSize: '2rem',
              border: imageUrl ? '2px solid #e0e0e0' : 'none'
            }}
          >
            {(!imageUrl && (user?.full_name || user?.username)?.charAt(0)?.toUpperCase()) || 'D'}
          </Avatar>
          <Box>
            <Typography variant="h6" gutterBottom>
              Welcome, Dr. {user?.full_name || user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user?.specialization || 'Specialist'} • {user?.hospital_name || 'Hospital'} • {user?.years_of_experience || 'Years of Experience'} Years
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

export default function DoctorDashboard() {
  const { user } = useAuth();
  const isFetchingRef = useRef(false);
  const dashboardPromiseRef = useRef<Promise<any> | null>(null);
  const healthPromiseRef = useRef<Promise<any> | null>(null);
  const trendsPromiseRef = useRef<Promise<any> | null>(null);
  const navigate = useNavigate();
  const [myPatients, setMyPatients] = useState<Patient[]>([]);
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [criticalPatients, setCriticalPatients] = useState<Patient[]>([]);
  const [updatingAppointment, setUpdatingAppointment] = useState<string | null>(null);

  // Analytics state
  const [healthStatusData, setHealthStatusData] = useState<any[]>([]);
  const [weeklyTrendsData, setWeeklyTrendsData] = useState<any[]>([]);
  const [totalAssignedPatients, setTotalAssignedPatients] = useState(0);
  const [totalCritical, setTotalCritical] = useState(0);
  const [trendsFilter, setTrendsFilter] = useState<string>('heartrate');
  // Loading state for initial page load
  const [isLoading, setIsLoading] = useState(true);

  // Analytics state
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState<string | null>(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const handleBulkDownload = async () => {
    try {
      if (!user?.id) return;
      setDownloadingReport(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/doctor-bulk-weekly`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/pdf'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = `Doctor_Weekly_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download bulk report');
        // Optionally set an error state to show a snackbar
      }
    } catch (error) {
      console.error('Error downloading bulk report:', error);
    } finally {
      setDownloadingReport(false);
    }
  };
  const [statusFilter, setStatusFilter] = useState<string>('heartrate');
  const [statusLoading, setStatusLoading] = useState(false);
  const [stableImageTimestamp] = useState(Date.now());




  // Fetch Patient Health Status from API
  const fetchHealthStatusData = useCallback(async () => {
    if (!user?.id) return;
    if (healthPromiseRef.current) return healthPromiseRef.current;

    healthPromiseRef.current = (async () => {
      try {
        setStatusLoading(true);
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/patient-health-status?activity_status=${statusFilter}`,
          config
        );
        const data = response.data;
        const chartData = Array.isArray(data) ? data : [
          { name: 'Needs Priority Review', value: data.critical || 0, color: '#f44336' },
          { name: 'Needs Review', value: data.warning || 0, color: '#ff9800' },
          { name: 'Within Usual Range', value: data.normal || 0, color: '#4caf50' }
        ];
        
        setHealthStatusData(chartData);
      } catch (error) {
        console.error('Error fetching health status pie chart data:', error);
        setHealthStatusData([
          { name: 'Needs Priority Review', value: 0, color: '#f44336' },
          { name: 'Needs Review', value: 0, color: '#ff9800' },
          { name: 'Within Usual Range', value: 0, color: '#4caf50' }
        ]);
      } finally {
        setStatusLoading(false);
        healthPromiseRef.current = null;
      }
    })();

    return healthPromiseRef.current;
  }, [statusFilter, user?.id]);

  const handleFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as string);
  };

  const fetchDashboardData = useCallback(async () => {
    if (!user?.id) return;
    if (dashboardPromiseRef.current) return dashboardPromiseRef.current;

    dashboardPromiseRef.current = (async () => {
      try {
        isFetchingRef.current = true;
        setPatientsLoading(true);
        setPatientsError(null);
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        console.log('🔍 Initiating Initial Dashboard Fetch...');

        // Use Promise.all to await both internal calls
        await Promise.allSettled([
          // 1. Fetch Summary (Counts + Appointments)
          axios.get(`${process.env.REACT_APP_API_URL}/doctor/dashboard/summary`, config)
            .then((res) => {
              if (res.data.status === 'Success') {
                const summary = res.data;
                setTotalAssignedPatients(summary.counts.total_patients || 0);
                setTotalCritical(summary.counts.total_critical || 0);

                const formattedAppointments = (summary.today_appointments || []).map((apt: any) => {
                  const appointmentDateTime = new Date(`${apt.appointment_date}T${apt.appointment_time}`);
                  return {
                    ...apt,
                    date: appointmentDateTime,
                    time: appointmentDateTime
                  };
                });
                setTodayAppointments(formattedAppointments);
              }
            })
            .catch(err => console.error('Summary fetch error:', err)),

          // 2. Fetch Health Dashboard Data (Patient List)
          axios.get(`${process.env.REACT_APP_API_URL}/health/dashboard?limit=100`, config)
            .then((res) => {
              const pData = res.data;
              const patientsData = Array.isArray(pData) ? pData : (pData.patients || pData.patients_with_data || []);

              const processedPatients = patientsData.map((p: any) => ({
                ...p,
                healthStatus: (p.health_status || p.status || 'normal').toLowerCase()
              }));

              setMyPatients(processedPatients);
              setCriticalPatients(processedPatients.filter((p: any) => p.healthStatus === 'critical'));
            })
            .catch(err => {
              console.error('Health data fetch error:', err);
              setPatientsError(err.response?.data?.detail || 'An error occurred while loading patient data.');
            })
            .finally(() => {
              setPatientsLoading(false);
            })
        ]);

      } catch (error: any) {
        console.error('Unexpected error initiating dashboard data fetch:', error);
      } finally {
        isFetchingRef.current = false;
        dashboardPromiseRef.current = null;
      }
    })();

    return dashboardPromiseRef.current;
  }, [user?.id]);

  const fetchTrendsData = useCallback(async () => {
    if (!user?.id) return;
    if (trendsPromiseRef.current) return trendsPromiseRef.current;

    trendsPromiseRef.current = (async () => {
      try {
        setTrendsLoading(true);
        setTrendsError(null);
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const trendsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/weekly-health-trends?days=7&activity_status=${trendsFilter}`,
          config
        );

        const trendsRaw = trendsRes.data;
        const trendsData = Array.isArray(trendsRaw) ? trendsRaw : (trendsRaw.trends || []);
        setWeeklyTrendsData(trendsData);
      } catch (error: any) {
        console.error('Error fetching trends data:', error);
        setTrendsError(error.response?.data?.detail || 'Failed to load trends data');
      } finally {
        setTrendsLoading(false);
        trendsPromiseRef.current = null;
      }
    })();

    return trendsPromiseRef.current;
  }, [user?.id, trendsFilter]);

  // Initial mount-only consolidated fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user?.id) return;
      
      setIsLoading(true);
      console.log('🏁 Starting Initial Dashboard Load...');
      
      try {
        // Use Promise.all to strictly wait for all initial data
        // This ensures the global loader stays up until everything is ready
        await Promise.all([
          fetchDashboardData(),
          fetchHealthStatusData(),
          fetchTrendsData()
        ]);
        
        console.log('✅ All initial data settled');
      } catch (error) {
        console.error('Error during initial dashboard load:', error);
      } finally {
        // Final guard: Ensure the dashboard only "opens" once we've had a chance to process everything
        setIsLoading(false);
        console.log('📱 Dashboard Final View Ready');
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // Only trigger localized update if initial load is finished AND user actually changed a filter
    if (user?.id && !isLoading) {
      console.log('🔄 Triggering localized Pie Chart update...');
      fetchHealthStatusData();
    }
  }, [statusFilter, user?.id, fetchHealthStatusData]);

  useEffect(() => {
    // Only trigger localized update if initial load is finished AND user actually changed a filter
    if (user?.id && !isLoading) {
      console.log('🔄 Triggering localized Trends update...');
      fetchTrendsData();
    }
  }, [trendsFilter, user?.id, fetchTrendsData]);

  const handleMarkAsCompleted = async (appointmentId: string) => {
    try {
      setUpdatingAppointment(appointmentId);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Update appointment status to completed
      await axios.put(
        `${process.env.REACT_APP_API_URL}/appointments/${appointmentId}`,
        { status: 'completed' },
        config
      );

      // Update local state
      setTodayAppointments(prevAppointments =>
        prevAppointments.map(apt =>
          apt.id === appointmentId ? { ...apt, status: 'completed' } : apt
        )
      );

      console.log('✅ Appointment marked as completed successfully');
    } catch (error) {
      console.error('❌ Error marking appointment as completed:', error);
      alert('Failed to mark appointment as completed. Please try again.');
    } finally {
      setUpdatingAppointment(null);
    }
  };

  const stats = {
    // Standardize patient count to match admin dashboard logic (from /patients endpoint)
    // Fallback to myPatients deduplicated count if totalAssignedPatients is 0
    totalPatients: totalAssignedPatients || new Set(myPatients.map(p => p.patient_id || p.id)).size,
    todayAppointments: todayAppointments.length,
    pendingAppointments: todayAppointments.filter(a => a.status === 'scheduled').length,
    criticalAlerts: totalCritical
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'normal': return 'success';
      default: return 'default';
    }
  };


  // Show global loading spinner while initial data is being fetched
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="textSecondary">
          Loading Dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom>
        Doctor Dashboard
      </Typography>



      {/* Doctor Profile */}
      <DoctorProfileCard
        user={user}
        imageUrl={getCacheBustedImageUrl(user?.profile_image, stableImageTimestamp)}
      />

      {/* Key Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)'
          },
          gap: 3,
          mb: 4
        }}
      >
        <StatCard
          title="My Patients"
          value={stats.totalPatients}
          icon={<People fontSize="large" />}
          color="primary.main"
          subtitle="Total assigned"
          onClick={() => navigate('/patients')}
        />
        <StatCard
          title="Trends Flagged"
          value={stats.criticalAlerts}
          icon={<Warning fontSize="large" />}
          color="error.main"
          subtitle="Flagged for review"
          onClick={() => navigate('/critical-patients')}
        />
        <StatCard
          title="Health Monitoring"
          value="Active"
          icon={<MonitorHeart fontSize="large" />}
          color="success.main"
          subtitle="Real-time tracking"
          onClick={() => navigate('/health-monitor')}
        />
      </Box>

      <Box sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, 1fr)'
        }
      }}>
        {/* Today's Appointments */}
        <Box>
          <Card sx={{ height: '100%' }}>
            <CardContent>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  onClick={() => navigate('/appointments')}
                  sx={{ 
                    cursor: 'pointer',
                    display: 'inline-block',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Today's Appointments
                </Typography>
              {todayAppointments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarToday sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No appointments scheduled for today
                  </Typography>
                </Box>
              ) : (
                <List>
                  {todayAppointments.map((appointment) => (
                    <ListItem key={appointment.id} divider>
                      <ListItemIcon>
                        <AccessTime color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${appointment.time ? appointment.time.toLocaleTimeString() : 'N/A'} - ${appointment.patientName}`}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Chip
                              label={appointment.type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={appointment.status}
                              size="small"
                              color={appointment.status === 'completed' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <Box>
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => handleMarkAsCompleted(appointment.id)}
                          disabled={appointment.status === 'completed' || updatingAppointment === appointment.id}
                          title={appointment.status === 'completed' ? 'Already completed' : 'Mark as completed'}
                        >
                          {updatingAppointment === appointment.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckCircle />
                          )}
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Patient Health Status Distribution */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography 
                  variant="h6"
                  onClick={() => navigate('/patient-status-list')}
                  sx={{ 
                    cursor: 'pointer',
                    display: 'inline-block',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Patient Health Status
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Vital Type</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Vital Type"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="heartrate">Heart Rate</MenuItem>
                    <MenuItem value="bp">Blood Pressure</MenuItem>
                    <MenuItem value="bmi">BMI Status</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {statusLoading || patientsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : patientsError ? (
                <Alert severity="error" sx={{ my: 2 }}>
                  {patientsError}
                </Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={healthStatusData.every(d => d.value === 0)
                        ? [{ name: 'No Data', value: 1, color: '#e0e0e0' }]
                        : healthStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={!healthStatusData.every(d => d.value === 0)}
                      label={({ cx, x, y, name, value, fill }) => {
                        if (healthStatusData.every(d => d.value === 0)) return 'No Data';
                        if (!value || value === 0) return null;

                        return (
                          <text
                            x={x}
                            y={y}
                            fill={fill}
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const statusMap: Record<string, string> = {
                                'Needs Priority Review': 'critical',
                                'Needs Review': 'warning',
                                'Within Usual Range': 'normal'
                              };
                              const status = statusMap[name] || 'critical';
                              navigate(`/patient-status-list?status=${status}&activity=${statusFilter}`);
                            }}
                          >
                            {`${name}: ${value}`}
                          </text>
                        );
                      }}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      onClick={(data) => {
                        if (data && data.name && data.name !== 'No Data') {
                          const statusMap: Record<string, string> = {
                            'Needs Priority Review': 'critical',
                            'Needs Review': 'warning',
                            'Within Usual Range': 'normal'
                          };
                          const status = statusMap[data.name] || 'critical';
                          navigate(`/patient-status-list?status=${status}&activity=${statusFilter}`);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {(healthStatusData.every(d => d.value === 0)
                        ? [{ name: 'No Data', value: 1, color: '#e0e0e0' }]
                        : healthStatusData
                      ).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend
                      onClick={(data) => {
                        if (data && data.value && data.value !== 'No Data') {
                          const statusMap: Record<string, string> = {
                            'Needs Priority Review': 'critical',
                            'Needs Review': 'warning',
                            'Within Usual Range': 'normal'
                          };
                          const status = statusMap[data.value] || 'critical';
                          navigate(`/patient-status-list?status=${status}&activity=${statusFilter}`);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Health Trends */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  📊 Weekly Health Trends
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="Download Clinic Weekly Summary">
                    <IconButton
                      onClick={handleBulkDownload}
                      disabled={downloadingReport}
                      size="small"
                      color="primary"
                    >
                      {downloadingReport ? <CircularProgress size={20} /> : <PictureAsPdf />}
                    </IconButton>
                  </Tooltip>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Vital Type</InputLabel>
                    <Select
                      value={trendsFilter}
                      label="Vital Type"
                      onChange={(e: SelectChangeEvent) => setTrendsFilter(e.target.value as string)}
                    >
                      <MenuItem value="heartrate">Heart Rate</MenuItem>
                      <MenuItem value="bp">Blood Pressure</MenuItem>
                      <MenuItem value="bmi">BMI Status</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              {trendsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : trendsError ? (
                <Alert severity="error" sx={{ my: 2 }}>
                  {trendsError}
                </Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={weeklyTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="critical" stackId="a" fill="#f44336" name="Needs Priority Review" />
                    <Bar dataKey="warning" stackId="a" fill="#ff9800" name="Needs Review" />
                    <Bar dataKey="normal" stackId="a" fill="#4caf50" name="Within Usual Range" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}